import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, signUp, confirmSignUp, resetPassword } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';

export type AuthMode = 'signin' | 'signup' | 'confirm' | 'resetRequest' | 'resetConfirm';
export type PasswordResetMode = 'request' | 'confirm';

// Contextual sign-in prompt derived from the ?next= URL.
function getContextualMessage(next: string): string | null {
  if (!next || next === '/') return null;
  const messages: Record<string, string> = {
    '/graph': 'Sign in to view your Knowledge Graph',
    '/interventions': 'Sign in to view Interventions',
    '/settings': 'Sign in to access Settings',
    '/team': 'Sign in to manage your Team',
  };
  return messages[next] || 'Sign in to continue';
}

/**
 * Owns all auth-form state and the sign-in / sign-up / confirm / password-reset
 * handlers. Behaviour is identical to the previous monolithic AuthWrapper — this
 * is purely an extraction for readability/testability. The authentication gate
 * (loading / redirect / fail-closed) stays in AuthWrapper itself.
 */
export function useAuthForm() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next') || '/';
  // Raw invitation token from the invite link (?inviteToken=...). Passed through
  // signup so the post-confirm trigger can verify it against the stored hash.
  const inviteToken = searchParams.get('inviteToken') || '';

  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [tosError, setTosError] = useState(false);

  // Password reset state
  const [passwordResetMode, setPasswordResetMode] = useState<PasswordResetMode>('request');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const contextualMessage = getContextualMessage(nextUrl);

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  useEffect(() => {
    // Don't clear email when transitioning to password reset from sign-in
    if (authMode !== 'resetRequest' && authMode !== 'resetConfirm') {
      setEmail('');
    }
    setPassword('');
    setError('');
    setSuccessMessage('');
    setConfirmPassword('');
    setConfirmationCode('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    if (authMode !== 'resetConfirm') {
      setPasswordResetMode('request');
    }
  }, [authMode]);

  useEffect(() => {
    setEmail('');
    setPassword('');
    setError('');
    setSuccessMessage('');
    setConfirmPassword('');
    setConfirmationCode('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordResetMode('request');
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      await signIn({ username: email, password });
      // Trigger AuthContext to re-check authentication
      await checkAuth();
      // Navigate to return URL after successful authentication
      navigate(decodeURIComponent(nextUrl));
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!tosChecked) {
      setTosError(true);
      return;
    }

    setTosError(false);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...(inviteToken ? { 'custom:inviteToken': inviteToken } : {}),
          },
        },
      });
      setAuthMode('confirm');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      await confirmSignUp({ username: email, confirmationCode });
      // Auto sign in after confirmation
      await signIn({ username: email, password });
      // Trigger AuthContext to re-check authentication
      await checkAuth();
      // Navigate to return URL after successful authentication
      navigate(decodeURIComponent(nextUrl));
    } catch (err: any) {
      setError(err.message || 'Failed to confirm sign up');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      await resetPassword({ username: email });
      setPasswordResetMode('confirm');
      setAuthMode('resetConfirm');
      setSuccessMessage('✓ Password reset code sent. Check your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword,
      });
      setAuthMode('signin');
      setPasswordResetMode('request');
      setSuccessMessage('✓ Password reset successful. Please sign in.');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    // Transition to password reset request mode
    setAuthMode('resetRequest');
    setPasswordResetMode('request');
    setError('');
    setSuccessMessage('');
  };

  return {
    // mode + contextual UI
    authMode,
    setAuthMode,
    contextualMessage,
    clearMessages,
    // shared fields
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    confirmationCode,
    setConfirmationCode,
    // status
    error,
    successMessage,
    isSubmitting,
    // terms of service
    tosChecked,
    setTosChecked,
    tosError,
    setTosError,
    // password reset
    passwordResetMode,
    verificationCode,
    setVerificationCode,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    // handlers
    handleSignIn,
    handleSignUp,
    handleConfirmSignUp,
    handleResetPasswordRequest,
    handleConfirmResetPassword,
    handleForgotPassword,
  };
}

export type AuthFormState = ReturnType<typeof useAuthForm>;
